import React, { useState, useEffect, useRef, useCallback } from "react";

import { useToast } from "../../hooks/use-toast";

import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import AddCarModal from "../templates/AddCarModal";
import Footer from "../templates/Footer";
import Header from "../templates/Header";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Mail,
  Phone,
  User,
  MessageSquare,
} from "lucide-react";

// Types
import { Car, CarCreate, Inquiry } from "../../types";
import { Toaster } from "../ui/toaster";
import ConfirmDialog from "../organisms/ConfirmDialog";
import ScrollUpPage from "../organisms/ScrollUpPage";
import { useDeleteVehicle, useVehicles } from "../../queries/vehicles.queries";
import { useInquiries } from "../../queries/inquiry.queries";
import CarDetailsModal from "../templates/CarDetailsModal";
import OverheadsManager from "../organisms/OverheadsManager";
import SalesReport from "../organisms/SalesReport";
import {
  formatDate,
  formatPrice,
  profitColor,
  scrollToTop,
} from "../../lib/utils";
import { useRole } from "../../context/RoleContext";
import { MANAGEMENT_ROLES, UserRole, hasRole } from "../../utils/general";

const PANEL_TITLES: Record<UserRole, string> = {
  [UserRole.ADMIN]: "Admin Panel",
  [UserRole.OWNER]: "Owner Panel",
  [UserRole.CLERK]: "Clerk Panel",
};

const AdminPanel: React.FC = () => {
  const { toast } = useToast();
  const role = useRole();
  const canManageVehicles = role === UserRole.ADMIN;
  const canViewFinance = hasRole(role, MANAGEMENT_ROLES);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [showAddCarForm, setShowAddCarForm] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    images: string[];
  } | null>(null);
  const [showPageUp, setShowPageUp] = useState(false);
  const [existingDetails, setExistingDetails] = useState<CarCreate>();
  const [isEdit, setIsEdit] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCar, setSelectedCar] = useState<Car | CarCreate | null>(null);

  const { data, fetchNextPage, isFetchingNextPage, hasNextPage } = useVehicles({ limit: 5 });
  const { data: inquiries } = useInquiries({ limit: 5 });
  const deleteMutation = useDeleteVehicle();

  const totalNumOfVehicles = data?.pages?.[0]?.total;
  const vehiclesResult: CarCreate[] = data?.pages.flatMap((p) => p.vehicles);
  const inquriesResult = inquiries?.pages.flatMap((p) => p.inquiries);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastCarElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage || !hasNextPage) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  const handleAddNewVehicle = () => {
    setExistingDetails(undefined);
    setShowAddCarForm(true);
    setIsEdit(false);
  };

  const handleEditVehicle = async (vehicleObject: CarCreate) => {
    setExistingDetails(vehicleObject);
    setShowAddCarForm(true);
    setIsEdit(true);
  };

  const handleShowDetails = (vehicleObject: CarCreate) => {
    setShowDetails((prev) => !prev);
    setSelectedCar(vehicleObject);
  };

  const removeVehicle = async (carId: string, images: string[]): Promise<void> => {
    try {
      deleteMutation.mutate({
        id: carId,
        images: images,
      });
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      toast({
        title: "Error",
        description: "Failed to delete vehicle",
        variant: "destructive",
      });
    } finally {
      toast({
        title: "Success!",
        description: "Vehicle deleted successfully",
      });
    }
  };

  useEffect(() => {
    const onScroll = () => {
      setShowPageUp(window.scrollY > 300);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="grid grid-cols-3 items-center mb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="col-start-2 flex justify-center">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 whitespace-nowrap">
            {PANEL_TITLES[role ?? UserRole.ADMIN]}
          </h1>
        </div>

        {canManageVehicles && (
          <div className="col-start-3 flex justify-end hidden md:flex">
            <Button onClick={() => handleAddNewVehicle()}>
              <Plus className="mr-2 h-4 w-4" />
              Add New Vehicle
            </Button>
          </div>
        )}
      </div>
      {canManageVehicles && (
        <div className="grid w-full px-4 md:hidden">
          <Button onClick={() => handleAddNewVehicle()}>
            <Plus className="mr-2 h-4 w-4" />
            Add New Vehicle
          </Button>
        </div>
      )}
      <div className="w-[95%] max-w-7xl mx-auto mt-4">
        <Tabs defaultValue="cars" className="w-full">
          <TabsList
            className={`grid w-full h-auto ${canViewFinance ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"}`}
          >
            <TabsTrigger value="cars">
              Manage Vehicles ({totalNumOfVehicles})
            </TabsTrigger>
            <TabsTrigger value="inquiries">
              Customer Inquiries
              {/* Customer Inquiries ({inquiries.length}) */}
            </TabsTrigger>
            {canViewFinance && (
              <>
                <TabsTrigger value="overheads">Overheads</TabsTrigger>
                <TabsTrigger value="report">Sales Report</TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="cars" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehiclesResult.map((car, index) => (
                <Card
                  key={car.id}
                  ref={
                    index === vehiclesResult.length - 1
                      ? lastCarElementRef
                      : undefined
                  }
                  className="group hover:shadow-lg transition-shadow"
                >
                  <div className="relative">
                    <img
                      src={car.images[0]}
                      alt={`${car.make} ${car.model}`}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge
                        variant={car.is_available ? "default" : "secondary"}
                      >
                        {car.is_available ? "Available" : "Sold"}
                      </Badge>
                    </div>
                  </div>

                  <CardContent className="p-4">
                    <h4 className="text-lg font-bold text-gray-900 mb-1">
                      {car.year} {car.make} {car.model}
                    </h4>
                    <p className="text-2xl font-bold text-blue-600 mb-3">
                      {formatPrice(car.price)}
                    </p>

                    {car.total_cost != null && (
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3 p-2 bg-gray-50 rounded-md">
                        <div className="text-gray-600">
                          Cost: {formatPrice(car.total_cost)}
                        </div>
                        <div
                          className={`text-right font-semibold ${profitColor(
                            car.profit ?? car.projected_profit,
                          )}`}
                        >
                          {car.profit != null ? "Profit" : "Est. profit"}:{" "}
                          {formatPrice(car.profit ?? car.projected_profit ?? 0)}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-4">
                      <div>Mileage: {car.mileage.toLocaleString()} km</div>
                      <div>Fuel: {car.fuel_type}</div>
                      <div>Transmission: {car.transmission}</div>
                      <div>Condition: {car.condition}</div>
                    </div>

                    <div className="flex space-x-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleShowDetails(car)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Details
                      </Button>
                      {canManageVehicles && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleEditVehicle(car)}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              setDeleteTarget({ id: car.id, images: car.images })
                            }
                            className="flex-1"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              <ConfirmDialog
                open={!!deleteTarget}
                title="Delete vehicle"
                message="This action cannot be undone."
                confirmText="Delete"
                onConfirm={() => {
                  if (!deleteTarget) return;
                  removeVehicle(deleteTarget.id, deleteTarget.images);
                  setDeleteTarget(null);
                }}
                onCancel={() => setDeleteTarget(null)}
              />
            </div>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <div className="space-y-4">
              {inquriesResult.map((inquiry) => {
                return (
                  <Card
                    key={inquiry.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedInquiry(inquiry)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <User className="h-4 w-4 text-gray-500" />
                            <span className="font-medium">
                              {inquiry.full_name}
                            </span>
                            <Badge variant="outline">
                              {formatDate(inquiry.createdAt)}
                            </Badge>
                          </div>

                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mb-2">
                            <div className="flex items-center min-w-0">
                              <Mail className="mr-1 h-3 w-3 shrink-0" />
                              <span className="break-all">{inquiry.email}</span>
                            </div>
                            <div className="flex items-center">
                              <Phone className="mr-1 h-3 w-3" />
                              {inquiry.phone_number}
                            </div>
                          </div>
                          <div className="flex items-start text-sm text-gray-700">
                            <MessageSquare className="mr-2 h-4 w-4 shrink-0 mt-0.5" />
                            <span className="min-w-0 break-words line-clamp-2">
                              {inquiry.message}
                            </span>
                          </div>
                          <div className="grid w-full mt-4 px-4 md:hidden">
                            <Button size="sm" variant="outline">
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </Button>
                          </div>
                        </div>
                        <div className="mt-4 px-4 hidden md:flex shrink-0">
                          <Button size="sm" variant="outline">
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {inquriesResult.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-xl font-medium text-gray-900 mb-2">
                    No inquiries yet
                  </h3>
                  <p className="text-gray-500">
                    Customer inquiries will appear here
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {canViewFinance && (
            <>
              <TabsContent value="overheads" className="mt-6">
                <OverheadsManager />
              </TabsContent>
              <TabsContent value="report" className="mt-6">
                <SalesReport />
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Add Vehicle Modal */}
        <AddCarModal
          showAddCarForm={showAddCarForm}
          setShowAddCarForm={setShowAddCarForm}
          vehicleDetails={existingDetails}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
        />

        {/* Inquiry Details Modal */}
        {selectedInquiry && (
          <Dialog
            open={!!selectedInquiry}
            onOpenChange={() => setSelectedInquiry(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Customer Inquiry Details</DialogTitle>
                <DialogDescription>
                  {formatDate(selectedInquiry.createdAt)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 min-w-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="min-w-0">
                    <Label>Customer Name</Label>
                    <p className="font-medium break-words">
                      {selectedInquiry.full_name}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <Label>Email</Label>
                    <p className="font-medium break-all">
                      {selectedInquiry.email}
                    </p>
                  </div>
                </div>

                <div>
                  <Label>Phone Number</Label>
                  <p className="font-medium">{selectedInquiry.phone_number}</p>
                </div>

                <div>
                  <Label>Message</Label>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-md whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">
                    {selectedInquiry.message}
                  </p>
                </div>

                <div className="flex space-x-2">
                  <Button className="flex-1">
                    <Mail className="mr-2 h-4 w-4" />
                    Reply via Email
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <Phone className="mr-2 h-4 w-4" />
                    Call Customer
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {showDetails && selectedCar && (
        <CarDetailsModal
          selectedCar={selectedCar}
          setSelectedCar={setSelectedCar}
        />
      )}
      {showPageUp && <ScrollUpPage handlePageUp={scrollToTop} />}
      <Footer />
      <Toaster />
    </div>
  );
};

export default AdminPanel;
